import { useState, useCallback } from 'react';
import { ApiError, ApiErrorType } from './errorTypes';

export interface UseErrorReturn {
  error: string | null;
  helpText: string | null;
  hasError: boolean;
  setError: (error: string | null) => void;
  clearError: () => void;
  handleError: (error: unknown, fallbackMessage?: string) => void;
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  executeWithErrorHandling: <T>(
    operation: () => Promise<T>,
    fallbackMessage?: string
  ) => Promise<T | null>;
}

/**
 * Simple, direct error handling hook
 * Better than event bus for most use cases
 */
export const useError = (): UseErrorReturn => {
  const [error, setError] = useState<string | null>(null);
  const [helpText, setHelpText] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(false);

  const clearError = useCallback(() => {
    setError(null);
    setHelpText(null);
  }, []);

  const categorizeError = useCallback((error: unknown, fallbackMessage?: string) => {
    if (error instanceof ApiError) {
      // For 5xx errors, override with a generic message (don't expose internal errors)
      const is5xxError = error.status && error.status >= 500;
      const message = is5xxError 
        ? 'Internal server error. Please try again later.'
        : (error.userMessage || error.message);
      
      let help = '';

      // Provide contextual help based on error type and status
      if (is5xxError) {
        help = 'Our servers are experiencing issues. Please try again in a few moments.';
      } else if (error.type === ApiErrorType.NetworkError || 
                (error.message.includes('Network Error') || error.message.includes('ERR_NETWORK'))) {
        help = 'Please check your internet connection and try again.';
      } else if (error.type === ApiErrorType.Unauthorized && error.errorCode === 'SESSION_EXPIRED') {
        help = 'Your session has expired. Please log in again.';
      } else if (error.status === 429) {
        help = 'Too many requests. Please wait a moment before trying again.';
      } else if (error.status && error.status >= 400 && error.status < 500) {
        // For 4xx errors, the backend message is already user-friendly, so minimal help text
        help = '';
      } else {
        help = 'Please try again or refresh the page if the problem persists.';
      }

      return { message, help };
    } else if (error instanceof Error) {
      // Network errors
      if (error.message.includes('Network Error') || error.message.includes('ERR_NETWORK')) {
        return {
          message: 'Connection problem. Please check your internet and try again.',
          help: 'Ensure you have a stable internet connection.'
        };
      } else {
        return {
          message: fallbackMessage || 'Something unexpected happened.',
          help: 'Please try again or refresh the page.'
        };
      }
    } else {
      return {
        message: fallbackMessage || 'An unexpected error occurred.',
        help: 'Please try again or refresh the page.'
      };
    }
  }, []);

  const handleError = useCallback((error: unknown, fallbackMessage?: string) => {
    console.error('Component error:', error);
    
    const { message, help } = categorizeError(error, fallbackMessage);
    setError(message);
    setHelpText(help);
  }, [categorizeError]);

  const executeWithErrorHandling = useCallback(async <T,>(
    operation: () => Promise<T>,
    fallbackMessage?: string
  ): Promise<T | null> => {
    try {
      setLoading(true);
      clearError();
      const result = await operation();
      return result;
    } catch (error) {
      handleError(error, fallbackMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [handleError, clearError]);

  return {
    error,
    helpText,
    hasError: !!error,
    setError,
    clearError,
    handleError,
    isLoading,
    setLoading,
    executeWithErrorHandling,
  };
};

/**
 * Hook for form validation errors
 */
export const useFormError = () => {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  const setFieldError = useCallback((field: string, message: string) => {
    setFieldErrors(prev => ({ ...prev, [field]: message }));
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setFieldErrors({});
    setGeneralError(null);
  }, []);

  const hasFieldError = useCallback((field: string) => {
    return !!fieldErrors[field];
  }, [fieldErrors]);

  const hasAnyError = Object.keys(fieldErrors).length > 0 || !!generalError;

  return {
    fieldErrors,
    generalError,
    setFieldError,
    clearFieldError,
    setGeneralError,
    clearAllErrors,
    hasFieldError,
    hasAnyError,
  };
};

/**
 * Hook for toast notifications (simple global errors)
 */
export const useToast = () => {
  const [toasts, setToasts] = useState<Array<{ 
    id: string; 
    message: string; 
    type: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
  }>>([]);

  const showToast = useCallback((
    message: string, 
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    duration: number = 5000
  ) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type, duration }]);
    
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showError = useCallback((message: string) => {
    showToast(message, 'error', 6000); // Errors stay longer
  }, [showToast]);

  const showSuccess = useCallback((message: string) => {
    showToast(message, 'success', 4000);
  }, [showToast]);

  return {
    toasts,
    showToast,
    showError,
    showSuccess,
    removeToast,
  };
}; 